/**
 * The asset's QC tab (1.0.0-rc.8): the summary of a run, its findings (click one to seek the player there), "Accept"
 * per finding with a comment (and Undo accept), the run history of every version, and the report. Run QC… in the header
 * runs once per version; administrators (qc.rerun) have Run QC again here (fixes after rc.9 testing). A run whose every
 * finding is accepted shows as passed, with the engine's own result kept beside it.
 */
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AlertOctagon, AlertTriangle, Check, FileJson, Loader2, Printer, RotateCcw } from 'lucide-react';
import { QC_CHECK_INFO, QC_GROUP_LABELS, QC_STATUS_LABELS, TICKS_PER_SECOND, type AssetDetail, type QcCheckId, type QcFindingDTO, type QcRunDTO } from '@prism/contracts';
import { api, errorMessage } from '../api/client';
import { useAssetQc, useMe, useQcRun } from '../api/hooks';
import { QcBadge, RunQcDialog, resolvedTitle } from '../components/QualityControl';
import { dateTime, relative } from '../lib/format';
import { toast } from '../components/Toasts';
import { TableScroll } from '../components/TableScroll';

const TRIGGER: Record<QcRunDTO['trigger'], string> = { watch: 'Watch folder', upload: 'Upload', workflow: 'Workflow', manual: 'Run QC…' };

export function QcTab({ asset, onSeek }: { asset: AssetDetail; onSeek: (seconds: number) => void }) {
  const q = useAssetQc(asset.id);
  const [params] = useSearchParams();
  const [selected, setSelected] = useState<string | null>(params.get('run'));
  const runs = q.data?.items ?? [];
  // The current version's latest run first; a run chosen in the history (or linked from a workflow run) instead.
  const shown = runs.find((r) => r.id === selected) ?? runs.find((r) => r.current) ?? runs[0] ?? null;
  useEffect(() => { if (params.get('run')) setSelected(params.get('run')); }, [params]);
  // A run per master (1.2.0): further masters are checked from here; the asset's QC status follows the primary.
  const me = useMe().data;
  const [onMaster, setOnMaster] = useState(false);
  const furtherMasters = (asset.masters ?? []).filter((m) => !m.primary);
  const masterRun = me?.permissions.includes('asset.qc') && !asset.deleted && furtherMasters.length > 0 && (
    <div className="qc-master-run" data-testid="qc-master-run">
      <button className="btn sm" onClick={() => setOnMaster(true)} data-testid="qc-run-master">Run QC on a master…</button>
      <span className="help">{furtherMasters.length} further master{furtherMasters.length === 1 ? '' : 's'}; their runs don’t change the asset’s QC status.</span>
      {onMaster && <RunQcDialog assetIds={[asset.id]} name={asset.title} masters={asset.masters} masterOnly onClose={() => setOnMaster(false)} />}
    </div>
  );
  if (q.isLoading) return <p className="muted"><Loader2 size={14} className="spin" /> Loading…</p>;
  if (!runs.length) {
    return (
      <div data-testid="qc-tab">
        <p className="muted" data-testid="qc-none">QC has not been run on this asset.</p>
        <p className="help">A watch folder or the uploads setting can run it on ingest, a workflow’s Run QC step on demand, and {q.data?.canRun ? 'you with Run QC… above' : 'a media manager with Run QC…'}. <Link to="/help/quality-control">About quality control</Link></p>
        {masterRun}
      </div>
    );
  }
  const anyMaster = runs.some((r) => r.master);
  return (
    <div data-testid="qc-tab" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {masterRun}
      {shown && <RunView run={shown} asset={asset} onSeek={onSeek} canAccept={Boolean(q.data?.canRun)}
        canRerun={Boolean(q.data?.canRerun) && q.data?.availability.reason === 'done' && shown.current} onRerunStarted={() => setSelected(null)} />}
      <section>
        <p className="section-label">Run history</p>
        <TableScroll>
        <table className="table compact" data-testid="qc-history">
          <thead><tr><th>Version</th>{anyMaster && <th>Master</th>}<th>Result</th><th>Profile</th><th>By</th><th>When</th></tr></thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id} className={shown?.id === r.id ? 'selected' : ''} style={{ cursor: 'pointer' }} onClick={() => setSelected(r.id)} data-testid="qc-history-row" data-status={r.status}>
                <td>v{r.versionNumber}{r.current ? '' : <span className="faint"> (earlier)</span>}</td>
                {anyMaster && <td className="muted" data-testid="qc-history-master">{r.master?.label ?? 'Primary'}</td>}
                <td><QcBadge status={r.effectiveStatus} resolved={r.resolved} />{r.summary ? <span className="faint" style={{ marginLeft: 6 }}>{r.summary.errors} / {r.summary.warnings}</span> : null}</td>
                <td className="wrap">{r.profile.name}</td>
                <td className="muted wrap">{TRIGGER[r.trigger]}{r.requestedBy ? ` · ${r.requestedBy}` : ''}</td>
                <td className="muted nowrap" title={dateTime(r.finishedAt ?? r.createdAt)}>{relative(r.finishedAt ?? r.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </TableScroll>
      </section>
    </div>
  );
}

function RunView({ run, asset, onSeek, canAccept, canRerun, onRerunStarted }: {
  run: QcRunDTO; asset: AssetDetail; onSeek: (s: number) => void; canAccept: boolean; canRerun: boolean; onRerunStarted: () => void;
}) {
  const detail = useQcRun(run.id);
  const [rerun, setRerun] = useState(false);
  const s = run.summary;
  const findings = detail.data?.findings ?? [];
  return (
    <section data-testid="qc-run" data-status={run.effectiveStatus} data-engine-status={run.status}>
      <div className="qc-summary">
        <QcBadge status={run.effectiveStatus} resolved={run.resolved} />
        <span><b>{run.profile.name}</b> · v{run.versionNumber} · {TRIGGER[run.trigger]}{run.master ? <span data-testid="qc-run-master-label"> ·{run.master.label}</span> : null}</span>
        <span className="muted" title={dateTime(run.finishedAt ?? run.createdAt)}>{run.finishedAt ? `Checked ${relative(run.finishedAt)}` : `Started ${relative(run.createdAt)}`}</span>
        <span style={{ flex: 1 }} />
        {canRerun && (
          <button className="btn ghost sm" onClick={() => setRerun(true)} data-testid="qc-rerun" title="Administrators can run QC again on a version already checked">
            <RotateCcw size={13} /> Run QC again
          </button>
        )}
        {rerun && <RunQcDialog assetIds={[asset.id]} name={asset.title} rerun={{ profileId: run.profile.id }} onClose={() => setRerun(false)} onDone={onRerunStarted} />}
        {run.finishedAt && run.status !== 'error' && (
          <>
            <a className="btn ghost sm" href={`/api/v1/qc/runs/${run.id}/report?format=html`} target="_blank" rel="noreferrer" data-testid="qc-report-html"><Printer size={13} /> Report</a>
            <a className="btn ghost sm" href={`/api/v1/qc/runs/${run.id}/report`} target="_blank" rel="noreferrer" data-testid="qc-report-json"><FileJson size={13} /> JSON</a>
          </>
        )}
      </div>
      {(run.status === 'queued' || run.status === 'running') && (
        <div className={`progress ${run.percent == null ? 'indeterminate' : ''}`} role="progressbar" aria-label="QC" style={{ marginTop: 10 }}><span style={{ width: `${run.percent ?? 0}%` }} /></div>
      )}
      {run.status === 'error' && run.error && <div className="banner error" style={{ marginTop: 10 }} data-testid="qc-error"><b>QC could not run.</b> {run.error.message} {run.current ? 'Run QC… is available again.' : ''}</div>}
      {s && (
        <dl className="kv" style={{ marginTop: 10 }} data-testid="qc-measures">
          <dt>Findings</dt><dd>{s.errors} error{s.errors === 1 ? '' : 's'}, {s.warnings} warning{s.warnings === 1 ? '' : 's'}{s.accepted ? ` · ${s.accepted} accepted` : ''}{s.truncated ? ' · more were left out' : ''}</dd>
          {run.resolved && (
            <><dt>Engine result</dt><dd data-testid="qc-engine-result" title={resolvedTitle(run.resolved)}>
              {QC_STATUS_LABELS[run.status].toLowerCase()}, {[s.errors ? `${s.errors} error${s.errors === 1 ? '' : 's'}` : '', s.warnings ? `${s.warnings} warning${s.warnings === 1 ? '' : 's'}` : ''].filter(Boolean).join(', ')}, all accepted
              <span className="faint"> (the last by {run.resolved.by ?? 'someone'} {relative(run.resolved.at)})</span>
            </dd></>
          )}
          {s.loudnessLufs != null && <><dt>Loudness</dt><dd data-testid="qc-loudness">{s.loudnessLufs} LUFS{s.truePeakDbtp != null ? ` · true peak ${s.truePeakDbtp} dBTP` : ''}{s.loudnessRange != null ? ` · LRA ${s.loudnessRange} LU` : ''}</dd></>}
          {s.skipped.length > 0 && <><dt>Not checked</dt><dd>{s.skipped.map((x) => `${QC_CHECK_INFO[x.check as QcCheckId]?.label ?? x.check} (${x.reason.toLowerCase()})`).join(', ')}</dd></>}
          {s.elapsedMs != null && <><dt>Took</dt><dd>{(s.elapsedMs / 1000).toFixed(1)} s · {s.engine}</dd></>}
        </dl>
      )}
      {run.finishedAt && run.status !== 'error' && (
        <>
          <p className="section-label" style={{ marginTop: 14 }}>Findings</p>
          {detail.isLoading ? <p className="muted"><Loader2 size={14} className="spin" /></p> : findings.length === 0
            ? <p className="muted" data-testid="qc-no-findings">No findings: every check passed.</p>
            : <ul className="qc-findings" data-testid="qc-findings">{findings.map((f) => <FindingRow key={f.id} f={f} run={run} asset={asset} onSeek={onSeek} canAccept={canAccept} />)}</ul>}
          <p className="help" style={{ marginTop: 8 }}>Timed findings are also in the QC timecode layer (Log tab) and in search. <Link to="/help/quality-control#findings">Reading findings</Link></p>
        </>
      )}
    </section>
  );
}

function FindingRow({ f, run, asset, onSeek, canAccept }: { f: QcFindingDTO; run: QcRunDTO; asset: AssetDetail; onSeek: (s: number) => void; canAccept: boolean }) {
  const qc = useQueryClient();
  const me = useMe().data;
  const [accepting, setAccepting] = useState(false);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const info = QC_CHECK_INFO[f.check as QcCheckId];
  const seekable = f.startTicks !== null && Boolean(asset.media.proxyUrl);
  async function accept() {
    setBusy(true);
    try {
      await api(`/api/v1/qc/runs/${run.id}/findings/${f.id}/accept`, { method: 'POST', json: { comment } });
      await qc.invalidateQueries({ queryKey: ['qc'] });
      toast('Finding accepted');
      setAccepting(false);
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  }
  // Undo an acceptance (fixes after rc.9 testing): whoever accepted it, or an administrator.
  const canUndo = Boolean(canAccept && me && f.accepted && (f.accepted.byId === me.user.id || me.permissions.includes('admin')));
  async function undo() {
    setBusy(true);
    try {
      await api(`/api/v1/qc/runs/${run.id}/findings/${f.id}/accept`, { method: 'DELETE' });
      await qc.invalidateQueries({ queryKey: ['qc'] });
      toast('Acceptance undone');
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  }
  return (
    <li className={`qc-finding ${f.accepted ? 'accepted' : ''}`} data-testid="qc-finding" data-check={f.check} data-severity={f.severity}>
      <span className={`sev-${f.severity}`} title={f.severity === 'error' ? 'Error' : 'Warning'}>{f.severity === 'error' ? <AlertOctagon size={14} /> : <AlertTriangle size={14} />}</span>
      {seekable
        ? <button type="button" className="link-button tc" onClick={() => onSeek(f.startTicks! / TICKS_PER_SECOND)} data-testid="qc-seek" title="Play from here">{f.timecode ?? `${(f.startTicks! / TICKS_PER_SECOND).toFixed(2)} s`}</button>
        : <span className="tc faint">{f.startTicks === null ? 'Whole file' : f.timecode}</span>}
      <span>
        <span className="faint">{info ? `${QC_GROUP_LABELS[info.group]} · ${info.label}` : f.check}: </span>{f.message}
        {f.accepted && <span className="faint" style={{ display: 'block' }} data-testid="qc-accepted"><Check size={11} /> Accepted by {f.accepted.by ?? 'someone'} {relative(f.accepted.at)}: “{f.accepted.comment}”</span>}
        {accepting && (
          <span style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <input className="input" style={{ flex: 1 }} autoFocus placeholder="Why it is acceptable" value={comment} onChange={(e) => setComment(e.target.value)} data-testid="qc-accept-comment"
              onKeyDown={(e) => { if (e.key === 'Enter' && comment.trim()) void accept(); }} />
            <button className="btn sm primary" disabled={busy || !comment.trim()} onClick={() => void accept()} data-testid="qc-accept-save">Accept</button>
            <button className="btn sm ghost" onClick={() => setAccepting(false)}>Cancel</button>
          </span>
        )}
      </span>
      {canAccept && me && !f.accepted && !accepting ? <button className="btn ghost sm" onClick={() => setAccepting(true)} data-testid="qc-accept">Accept</button>
        : canUndo ? <button className="btn ghost sm" disabled={busy} onClick={() => void undo()} data-testid="qc-unaccept" title="Undo this acceptance">Undo accept</button> : <span />}
    </li>
  );
}

